'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Clock, FileText, CheckCircle2, Pencil, X, Sparkles } from 'lucide-react'
import { GlassCard } from '@/components/ui/glass-card'
import { StatusPill } from '@/components/ui/status-pill'
import { SectionHeader } from '@/components/ui/section-header'
import { PageShell } from '@/components/ui/page-shell'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { cn } from '@/lib/utils'

interface Project {
  id: string
  name: string
}

interface DailyWork {
  id: string
  date: string
  title: string
  description: string
  hoursWorked: number
  projectId: string | null
}

interface EmployeeDailyWorkProps {
  userId: string
}

const MIN_WORDS = 50

function wordCount(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function EmployeeDailyWork({ userId }: EmployeeDailyWorkProps) {
  const [todayWork, setTodayWork] = useState<DailyWork | null>(null)
  const [history, setHistory] = useState<DailyWork[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [hasCheckedOut, setHasCheckedOut] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: 'none',
    hoursWorked: 8
  })
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    projectId: 'none',
    hoursWorked: 8
  })

  const fetchData = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const startDate = thirtyDaysAgo.toISOString().split('T')[0]
      const endDate = today

      const [workResult, projectsResult, historyResult] = await Promise.allSettled([
        apiFetch(`/api/daily-work?date=${today}`).then(r => r.json()),
        apiFetch('/api/projects').then(r => r.json()),
        apiFetch(`/api/daily-work?startDate=${startDate}&endDate=${endDate}`).then(r => r.json())
      ])

      if (workResult.status === 'fulfilled') {
        const workData = workResult.value
        if (workData.dailyWorks && workData.dailyWorks.length > 0) {
          setTodayWork(workData.dailyWorks[0])
          setFormData({
            title: workData.dailyWorks[0].title,
            description: workData.dailyWorks[0].description,
            projectId: workData.dailyWorks[0].projectId || 'none',
            hoursWorked: workData.dailyWorks[0].hoursWorked
          })
        } else {
          setTodayWork(null)
        }
      } else {
        console.error("Error fetching today's work:", workResult.reason)
        toast.error(workResult.reason?.message || "Failed to fetch today's work")
      }

      if (historyResult.status === 'fulfilled') {
        if (historyResult.value.dailyWorks) setHistory(historyResult.value.dailyWorks)
      } else {
        console.error('Error fetching work history:', historyResult.reason)
        toast.error(historyResult.reason?.message || 'Failed to fetch work history')
      }

      if (projectsResult.status === 'fulfilled') {
        if (projectsResult.value.projects) setProjects(projectsResult.value.projects)
      } else {
        console.error('Error fetching projects:', projectsResult.reason)
        toast.error(projectsResult.reason?.message || 'Failed to fetch projects')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!userId) return
    let active = true
    const checkStatus = async () => {
      try {
        const today = new Date().toISOString().split('T')[0]
        const res = await apiFetch(`/api/attendance?date=${today}`)
        const data = await res.json()
        if (!active) return
        const rec = (data.attendance || [])[0]
        setHasCheckedOut(!!rec?.checkOut)
      } catch (e) { console.error(e) }
    }
    checkStatus()
    const t = setInterval(checkStatus, 30000)
    return () => { active = false; clearInterval(t) }
  }, [userId])

  const startEditing = (work: DailyWork) => {
    setEditingId(work.id)
    setEditFormData({
      title: work.title,
      description: work.description,
      projectId: work.projectId || 'none',
      hoursWorked: work.hoursWorked,
    })
  }

  const handleEditSubmit = async (work: DailyWork) => {
    setSubmitting(true)
    try {
      await apiFetch('/api/daily-work', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editFormData,
          date: work.date,
          projectId: editFormData.projectId === 'none' ? null : editFormData.projectId,
        }),
      })
      toast.success('Work log updated')
      setEditingId(null)
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to update')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const res = await apiFetch('/api/daily-work', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          projectId: formData.projectId === 'none' ? null : formData.projectId
        })
      })
      toast.success(todayWork ? 'Daily work updated' : 'Daily work saved')
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to save daily work')
    } finally {
      setSubmitting(false)
    }
  }

  const todayLabel = (() => {
    const d = new Date()
    return `${d.toLocaleDateString('en-US', { weekday: 'long' })}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  })()

  const words = wordCount(formData.description)
  const wordsOk = words >= MIN_WORDS

  return (
    <PageShell>
      <SectionHeader
        icon={FileText}
        title='Daily Work Log'
        description={todayLabel}
      />

      {loading ? (
        <TabLoader />
      ) : (
        <>
      {!todayWork && (
        <GlassCard tone='orange'>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500/15 text-orange-600 dark:text-orange-300'>
              <Sparkles className='h-5 w-5' />
            </div>
            <div className='min-w-0 flex-1'>
              <p className='text-sm font-medium text-neutral-900 dark:text-white/90'>You haven&apos;t logged today&apos;s work yet</p>
              <p className='text-xs text-neutral-500 dark:text-white/50'>Take a minute to record what you worked on</p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Editor */}
      <GlassCard tone={todayWork ? 'green' : 'orange'}>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2'>
            <FileText className='h-4 w-4 text-orange-600 dark:text-orange-300' />
            <h3 className='text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90'>
              {todayWork ? "Update today's log" : "Log today's work"}
            </h3>
          </div>
          {todayWork && <StatusPill tone='green'><CheckCircle2 className='h-3 w-3' />Logged</StatusPill>}
        </div>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='title' className='text-neutral-700 dark:text-white/70'>Work title</Label>
            <Input
              id='title'
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder='Brief title of your work today'
              required
            />
          </div>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='description' className='text-neutral-700 dark:text-white/70'>Description</Label>
              <span className={cn(
                'text-[11px] font-medium tabular-nums',
                wordsOk ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'
              )}>
                {words} / {MIN_WORDS} words {wordsOk && '✓'}
              </span>
            </div>
            <Textarea
              id='description'
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder='Describe what you worked on today — specific tasks, decisions, blockers...'
              required
              rows={5}
            />
            <div className='h-1.5 rounded-full bg-neutral-50 dark:bg-white/5 overflow-hidden'>
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  wordsOk ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : 'bg-gradient-to-r from-amber-500 to-amber-400'
                )}
                style={{ width: `${Math.min(100, (words / MIN_WORDS) * 100)}%` }}
              />
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label className='text-neutral-700 dark:text-white/70'>Project (optional)</Label>
              <Select value={formData.projectId} onValueChange={(value) => setFormData({ ...formData, projectId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder='Select project' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='none'>No Project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='hoursWorked' className='text-neutral-700 dark:text-white/70'>Hours worked</Label>
              <Input
                id='hoursWorked'
                type='number'
                min='0'
                max='24'
                step='0.5'
                value={formData.hoursWorked}
                onChange={(e) => setFormData({ ...formData, hoursWorked: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <Button
            type='submit'
            disabled={submitting}
            className={cn(PRIMARY_BTN, 'w-full sm:w-auto')}
          >
            {submitting ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className='mr-2 h-4 w-4' />
                {todayWork ? 'Update Log' : 'Save Log'}
              </>
            )}
          </Button>
        </form>
      </GlassCard>

      {/* History */}
      <div>
        <div className='flex items-center justify-between mb-3'>
          <div>
            <h3 className='text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90'>Work history</h3>
            <p className='text-[11px] text-neutral-400 dark:text-white/40'>Last 30 days</p>
          </div>
        </div>

        {history.length === 0 ? (
          <GlassCard>
            <EmptyState
              icon={FileText}
              title='No work logs yet'
              description='Logs you save will show up here for the last 30 days.'
            />
          </GlassCard>
        ) : (
          <div className='space-y-3'>
            {history.map((work) => {
              const isToday = work.date === new Date().toISOString().split('T')[0]
              const canEdit = !hasCheckedOut && isToday
              const isEditing = editingId === work.id

              if (isEditing) {
                return (
                  <GlassCard key={work.id} tone='orange'>
                    <div className='flex items-center justify-between mb-3'>
                      <span className='text-neutral-600 dark:text-white/60 text-sm'>
                        Editing — {(() => { const d = new Date(work.date + 'T00:00:00'); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}
                      </span>
                      <Button variant='ghost' size='sm' onClick={() => setEditingId(null)} className='text-neutral-400 dark:text-white/40 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-50 dark:bg-white/5 h-8 w-8 p-0'>
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                    <div className='space-y-3'>
                      <Input
                        value={editFormData.title}
                        onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                        placeholder='Work title'
                      />
                      <Textarea
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                        placeholder='Description'
                        rows={4}
                      />
                      <div className='flex items-center gap-3'>
                        <Input
                          type='number'
                          min='0'
                          max='24'
                          step='0.5'
                          value={editFormData.hoursWorked}
                          onChange={(e) => setEditFormData({ ...editFormData, hoursWorked: parseFloat(e.target.value) || 0 })}
                          className='w-24'
                        />
                        <span className='text-neutral-500 dark:text-white/50 text-sm'>hours</span>
                        <div className='flex-1' />
                        <Button
                          size='sm'
                          disabled={submitting}
                          onClick={() => handleEditSubmit(work)}
                          className={PRIMARY_BTN}
                        >
                          {submitting ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Save'}
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                )
              }

              return (
                <GlassCard key={work.id} tone={isToday ? 'orange' : 'default'}>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='space-y-1.5 flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <Calendar className='h-3.5 w-3.5 text-neutral-400 dark:text-white/40' />
                        <span className='text-neutral-600 dark:text-white/60 text-xs tabular-nums'>{(() => { const d = new Date(work.date + 'T00:00:00'); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}</span>
                        <StatusPill tone='cyan'><Clock className='h-3 w-3' />{work.hoursWorked}h</StatusPill>
                        {isToday && <StatusPill tone='orange'>Today</StatusPill>}
                      </div>
                      <p className='text-sm font-medium text-neutral-900 dark:text-white/90 line-clamp-1'>{work.title}</p>
                      <p className='text-sm text-neutral-600 dark:text-white/60 whitespace-pre-wrap line-clamp-3'>{work.description}</p>
                    </div>
                    {canEdit && (
                      <Button variant='ghost' size='sm' onClick={() => startEditing(work)} className='text-neutral-400 dark:text-white/40 hover:text-orange-600 dark:hover:text-orange-300 hover:bg-orange-500/10 shrink-0'>
                        <Pencil className='h-4 w-4' />
                      </Button>
                    )}
                  </div>
                </GlassCard>
              )
            })}
          </div>
        )}
      </div>
        </>
      )}
    </PageShell>
  )
}
